"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const carePlans = [{
  id: "nursing",
  tabLabel: "專業護理",
  title: "專業居家護理服務",
  description: "由資深註冊護士及登記護士主理，將醫院級別的專業照護帶到家中。我們專注於精準的臨床護理，讓服務使用者能夠在熟悉的環境中獲得最高質素的醫療支援，同時為家屬提供專業指導。",
  image: "https://wanted-kite.10web.cloud/wp-content/uploads/2026/03/1773076144190-scaled.png?v=od1dikqs4y9",
  feature1: "傷口專業護理及拆線換藥",
  feature2: "藥物管理、注射及藥物諮詢",
  feature3: "各類導管護理 (如胃喉、尿喉)",
  feature4: "生命表徵監測與健康數據記錄",
  feature5: "上門抽血及化驗樣本收集服務"
}, {
  id: "rehab",
  tabLabel: "專職治療",
  title: "物理及專職復康治療",
  description: "專業治療師團隊包括物理、職業及言語治療師，為服務使用者量身定制個人化復康方案。透過針對性訓練，協助恢復身體機能，重建自主生活能力，提升生活質素。",
  image: "https://wanted-kite.10web.cloud/wp-content/uploads/2026/03/卡通化_深紫制服-1-scaled.png?v=3itka0i5zp8",
  feature1: "物理治療評估及功能運動訓練",
  feature2: "職業治療及家居安全改裝建議",
  feature3: "言語障礙評估及吞嚥能力訓練",
  feature4: "各類痛症舒緩及物理因子理療",
  feature5: "復康輔助器材使用及挑選指導"
}, {
  id: "daily",
  tabLabel: "日常照顧",
  title: "全方位日常起居照顧",
  description: "由受過專業訓練的保健員及護理員提供溫馨、尊嚴的日常照顧。我們不僅關注身體清潔，更重視服務使用者的心理健康，致力於營造舒適且安全的居家生活環境。",
  image: "https://wanted-kite.10web.cloud/wp-content/uploads/2026/03/卡通化_深紫制服_2K.png?v=67iosckh6s",
  feature1: "協助個人衛生、沐浴及更衣服務",
  feature2: "專業扶抱、轉移及壓瘡預防技巧",
  feature3: "認知障礙症 (失智症) 專項照顧",
  feature4: "陪伴外出、散步及社交活動參與",
  feature5: "基本家居環境整理及安全巡查"
}, {
  id: "meals",
  tabLabel: "陪診膳食",
  title: "陪診支援及營養膳食",
  description: "結合專業陪診與營養管理，由註冊營養師指導膳食安排。我們確保服務使用者在求診過程安全無憂，並能享用符合健康需求、營養均衡且易於咀嚼的美味餐點。",
  image: "https://wanted-kite.10web.cloud/wp-content/uploads/2026/03/Image-14-scaled.png?v=53x9gu3myq3",
  feature1: "門診預約、陪同就診及藥物領取",
  feature2: "註冊營養師個人化飲食評估",
  feature3: "軟餐、碎餐製作指導及營養調配",
  feature4: "管飼營養液調配及安全餵食監控",
  feature5: "慢性病健康飲食教育及體重管理"
}];
export default function ComprehensiveHomeCarePlan() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="73546" id="全方位到戶支援" className="py-24 bg-background text-foreground overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            全方位到戶支援計劃
          </h2>
          <div className="w-20 h-1.5 bg-primary/40 mx-auto mb-8 rounded-full"></div>
          <p className="text-xl text-muted-foreground leading-relaxed">
            憬芯醫療 (Prospect Care) 整合專業醫護資源，提供多元化的上門復康與照護服務，
            以「芯」出發，全方位守護您與家人的健康未來。
          </p>
        </div>

        <div ref={contentRef} className={`transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Tabs defaultValue="nursing" className="w-full flex flex-col items-center">
            <TabsList className="mb-12 bg-secondary/50 p-1.5 rounded-full flex-wrap justify-center h-auto backdrop-blur-sm border border-primary/10">
              {carePlans.map((plan, index) => <TabsTrigger key={plan.id} value={plan.id} data-index={index} className="rounded-full px-8 py-3.5 text-base font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-xl transition-all duration-300">
                  {plan.tabLabel}
                </TabsTrigger>)}
            </TabsList>

            <div className="w-full">
              {carePlans.map((plan, index) => <TabsContent key={plan.id} value={plan.id} data-index={index} className="mt-0 outline-none focus-visible:ring-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div className="order-2 lg:order-1 space-y-8">
                      <div>
                        <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                          {plan.title}
                        </h3>
                        <p className="text-muted-foreground text-lg leading-relaxed font-medium">
                          {plan.description}
                        </p>
                      </div>

                      <ul className="space-y-4">
                        {[plan.feature1, plan.feature2, plan.feature3, plan.feature4, plan.feature5].map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-4 p-5 rounded-2xl bg-secondary/30 border border-primary/5 hover:bg-secondary/60 hover:shadow-md transition-all duration-300">
                              <CircleCheck className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                              <span className="text-foreground font-semibold text-lg">{feature}</span>
                            </li>)}
                      </ul>
                    </div>

                    <div className="order-1 lg:order-2 relative">
                      <div className="absolute inset-0 bg-primary/10 rounded-[2.5rem] transform translate-x-6 translate-y-6 -z-10 blur-sm"></div>
                      <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border border-primary/20 aspect-[4/3]">
                        <img src={plan.image} alt={plan.title} className="w-full h-full object-cover hover:scale-110 transition-transform duration-1000 ease-in-out" />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none"></div>
                      </div>
                    </div>
                  </div>
                </TabsContent>)}
            </div>
          </Tabs>
        </div>
      </div>
    </section>;
}